import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance as api } from "@/lib/axios";
import { Message, Conversation, CreateMessageData } from "@/types";
import { getSecureUser } from "@/utils/secureStorage";

// Get all conversations for the current user
export const useConversations = () => {
  const user = getSecureUser();
  
  console.log("🔄 [CONVERSATIONS] Hook initialized:", {
    userId: user?.id,
    userRole: user?.role,
    userCompanyId: user?.companyId,
    userExists: !!user
  });
  
  const query = useQuery({
    queryKey: ["conversations", user?.id, user?.role],
    queryFn: async (): Promise<Conversation[]> => {
      console.log("🔄 [CONVERSATIONS] Query function called - fetching conversations for user:", {
        userId: user?.id,
        userRole: user?.role,
        userCompanyId: user?.companyId
      });
      console.log("🔄 [CONVERSATIONS] Full user object:", user);
      
      try {
        const response = await api.get("/api/v1/messages/my-conversations");
        console.log("✅ [CONVERSATIONS] Success response:", {
          status: response.status,
          data: response.data,
          conversationsCount: response.data?.data?.documents?.length || 0,
          conversations: response.data?.data?.documents?.map((conv: any) => ({
            id: conv.id,
            chatWithId: conv.chatWith?.id,
            chatWithName: conv.chatWith?.displayName,
            chatWithType: conv.chatWith?.type,
            lastMessage: conv.lastMessage,
            unreadCount: conv.unreadCount
          }))
        });
        return response.data.data.documents;
      } catch (error: any) {
        console.error("❌ [CONVERSATIONS] Error fetching conversations:", {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });
        throw error;
      }
    },
    enabled: !!user,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  console.log("🔄 [CONVERSATIONS] Query state:", {
    enabled: !!user,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error?.message,
    data: query.data?.length || 0
  });

  return query;
};

// Get messages for a specific conversation
export const useMessages = (chatWithId: string) => {
  return useQuery({
    queryKey: ["messages", chatWithId],
    queryFn: async (): Promise<Message[]> => {
      console.log("🔄 [MESSAGES] Fetching messages for chatWithId:", chatWithId);
      
      try {
        const response = await api.get(`/api/v1/messages/chat-with/${chatWithId}`);
        console.log("✅ [MESSAGES] Success response:", {
          status: response.status,
          chatWithId: chatWithId,
          messagesCount: response.data?.data?.documents?.length || 0,
          messages: response.data?.data?.documents?.map((msg: any) => ({
            id: msg._id,
            senderId: msg.sender?.id,
            senderName: msg.sender?.displayName,
            recipientId: msg.recipient?.id,
            recipientName: msg.recipient?.displayName,
            content: msg.content?.text,
            isRead: msg.isRead,
            createdAt: msg.createdAt
          }))
        });
        return response.data.data.documents;
      } catch (error: any) {
        console.error("❌ [MESSAGES] Error fetching messages:", {
          chatWithId: chatWithId,
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });
        throw error;
      }
    },
    enabled: !!chatWithId,
    refetchInterval: 10000, // Refetch every 10 seconds
  });
};

// Create a new message
export const useCreateMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageData: CreateMessageData): Promise<Message> => {
      // Get current user info to determine sender type
      const user = getSecureUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const isCompany = user.role === 'company_admin';
      
      const payload = {
        sender: user.id,
        recipient: messageData.recipient,
        senderModel: isCompany ? 'Company' : 'User',
        recipientModel: messageData.recipientModel || 'User',
        content: messageData.content
      };

      console.log("📤 [SEND_MESSAGE] Sending message with payload:", {
        sender: payload.sender,
        recipient: payload.recipient,
        senderModel: payload.senderModel,
        recipientModel: payload.recipientModel,
        content: payload.content,
        userRole: user.role,
        userCompanyId: user.companyId
      });
      
      try {
        const response = await api.post("/api/v1/messages", payload);
        console.log("✅ [SEND_MESSAGE] Success response:", {
          status: response.status,
          messageId: response.data?.data?.document?._id,
          message: response.data?.data?.document
        });
        return response.data.data.document;
      } catch (error: any) {
        console.error("❌ [SEND_MESSAGE] Error sending message:", {
          payload: payload,
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });
        throw error;
      }
    },
    onMutate: async (messageData) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ["messages", messageData.recipient] });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData(["messages", messageData.recipient]);

      // Get current user info for optimistic update
      const user = getSecureUser();
      if (!user) return;

      // Create optimistic message
      const optimisticMessage: Message = {
        _id: `temp-${Date.now()}`,
        sender: {
          id: user.id,
          displayName: user.fullName || user.name || "You",
          profilePhoto: user.profilePhoto || user.photo,
        },
        recipient: {
          id: messageData.recipient,
          displayName: "Recipient",
          profilePhoto: undefined,
        },
        content: messageData.content,
        isRead: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update the messages cache
      queryClient.setQueryData(
        ["messages", messageData.recipient],
        (oldMessages: Message[] = []) => [...oldMessages, optimisticMessage]
      );

      // Return a context object with the snapshotted value
      return { previousMessages };
    },
    onError: (_err, messageData, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousMessages) {
        queryClient.setQueryData(["messages", messageData.recipient], context.previousMessages);
      }
    },
    onSuccess: (newMessage, variables, _context) => {
      // Remove the optimistic message and replace with the real one
      queryClient.setQueryData(
        ["messages", variables.recipient],
        (oldMessages: Message[] = []) => {
          // Remove optimistic message and add real message
          const filteredMessages = oldMessages.filter(msg => !msg._id.startsWith('temp-'));
          return [...filteredMessages, newMessage];
        }
      );

      // Update conversations cache
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onSettled: (_data, _error, variables) => {
      console.log("🔄 [SEND_MESSAGE] Message settled, invalidating caches:", {
        recipient: variables.recipient,
        data: _data,
        error: _error
      });
      
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ["messages", variables.recipient] });
      // Also invalidate conversations to ensure the conversation list is updated
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      
      console.log("✅ [SEND_MESSAGE] Cache invalidation completed");
    },
  });
};

// Mark messages as read
export const useMarkMessagesAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatWithId: string): Promise<void> => {
      await api.post(`/api/v1/messages/mark-messages-as-read/${chatWithId}`);
    },
    onSuccess: (_, chatWithId) => {
      // Update the messages cache to mark messages as read
      queryClient.setQueryData(
        ["messages", chatWithId],
        (oldMessages: Message[] = []) =>
          oldMessages.map((msg) => ({ ...msg, isRead: true }))
      );

      // Update conversations cache to reset unread count
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
};
